
import React,{useEffect,useState}from"react";
import{
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery
}from"@mui/material";
import{useTheme}from"@mui/material/styles";
import LogoutRoundedIcon from"@mui/icons-material/LogoutRounded";
import DashboardRoundedIcon from"@mui/icons-material/DashboardRounded";
import DarkModeRoundedIcon from"@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from"@mui/icons-material/LightModeRounded";
import KeyboardDoubleArrowLeftRoundedIcon from"@mui/icons-material/KeyboardDoubleArrowLeftRounded";
import KeyboardDoubleArrowRightRoundedIcon from"@mui/icons-material/KeyboardDoubleArrowRightRounded";
import KeyboardArrowDownRoundedIcon from"@mui/icons-material/KeyboardArrowDownRounded";
import{useNavigate}from"react-router-dom";
import{useAuth}from"../../../context/AuthContext";
import{useDashboardTheme}from"../../../context/DashboardThemeContext";
import userIcon from"../../../static/media/icon/user.svg";
import briefcaseIcon from"../../../static/media/icon/briefcase.svg";
import registerIcon from"../../../static/media/icon/register.svg";

export default function TopBar({sidebarOpen=false,onToggleSidebar=()=>{}}){
  const navigate=useNavigate();
  const muiTheme=useTheme();
  const isMobile=useMediaQuery(muiTheme.breakpoints.down("sm"));
  const{user,logout,accessToken}=useAuth();
  const{dashboardTheme,toggleThemeMode}=useDashboardTheme();

  const[anchorEl,setAnchorEl]=useState(null);
  const[ambienteSri,setAmbienteSri]=useState("2");

  const menuOpen=Boolean(anchorEl);
  const permissions=user?.permissions||[];
  const isAdmin=user?.role==="super_admin"||user?.role==="admin";

  const canViewEnterprise=isAdmin||permissions.includes("enterprise.view");
  const canViewVentas=isAdmin||permissions.includes("ventas.view");

  const isDark=dashboardTheme.theme_mode==="dark";
  const themeLabel=isDark?"Modo claro":"Modo oscuro";
  const dotColor=ambienteSri==="2"?"#4CAF50":"#F44336";

  const textColor=dashboardTheme.text_color;
  const mutedColor=dashboardTheme.muted_text_color;
  const primaryColor=dashboardTheme.primary_color;
  const backgroundColor=dashboardTheme.card_background;
  const borderColor=dashboardTheme.border_color;
  const hoverColor=`${primaryColor}18`;

  const iconFilter=isDark?"brightness(0) invert(1)":"none";

  useEffect(()=>{
    if(!accessToken)return;

    const controller=new AbortController();

    const loadAmbienteSri=async()=>{
      try{
        const response=await fetch("/api/facturacion/sri/ambiente/",{
          method:"GET",
          headers:{
            Authorization:`Bearer ${accessToken}`
          },
          signal:controller.signal
        });

        if(!response.ok)return;

        const data=await response.json();
        const ambiente=String(data?.ambiente||"").trim();

        setAmbienteSri(ambiente==="2"?"2":"1");
      }catch(error){
        if(error?.name!=="AbortError"){
          setAmbienteSri("2");
        }
      }
    };

    loadAmbienteSri();

    return()=>{
      controller.abort();
    };
  },[accessToken]);

  const closeMenu=()=>{
    setAnchorEl(null);
  };

  const handleSidebarToggle=()=>{
    if(typeof onToggleSidebar==="function"){
      onToggleSidebar();
    }
  };

  const goToPanel=()=>{
    closeMenu();
    navigate("/admin-panel");
  };

  const goToCompany=()=>{
    closeMenu();
    navigate("/admin-panel/empresa");
  };

  const goToCaja=()=>{
    closeMenu();
    navigate("/admin-panel/caja");
  };

  const handleToggleTheme=()=>{
    closeMenu();
    toggleThemeMode();
  };

  const closeSession=async()=>{
    closeMenu();

    try{
      await logout();
    }finally{
      navigate("/iniciar-sesion",{replace:true});
    }
  };

  const iconButtonSx={
    width:40,
    height:40,
    borderRadius:"10px",
    color:textColor,
    boxShadow:"none",
    "&:hover":{
      bgcolor:hoverColor,
      boxShadow:"none"
    }
  };

  const menuItemSx={
    display:"flex",
    alignItems:"center",
    gap:1.3,
    minHeight:43,
    px:1.8,
    py:1,
    fontSize:13,
    fontWeight:500,
    color:textColor,
    borderRadius:"8px",
    mx:0.6,
    "&:hover":{
      bgcolor:hoverColor
    }
  };

  const imageStyles={
    width:21,
    height:21,
    objectFit:"contain",
    display:"block",
    filter:iconFilter,
    flexShrink:0
  };

  return(
    <Box
      component="header"
      sx={{
        height:76,
        minHeight:76,
        width:"100%",
        boxSizing:"border-box",
        bgcolor:backgroundColor,
        borderBottom:`1px solid ${borderColor}`,
        display:"flex",
        alignItems:"center",
        justifyContent:"space-between",
        gap:{xs:0.5,sm:1.5},
        px:{xs:1.2,sm:2,md:3},
        position:"sticky",
        top:0,
        zIndex:20
      }}
    >
      <Stack
        direction="row"
        spacing={0.7}
        alignItems="center"
        sx={{
          minWidth:0,
          flexShrink:0
        }}
      >
        <Tooltip
          title={sidebarOpen?"Contraer menú":"Expandir menú"}
          arrow
          disableInteractive
        >
          <IconButton
            onClick={handleSidebarToggle}
            aria-label={sidebarOpen?"Contraer menú lateral":"Expandir menú lateral"}
            aria-expanded={Boolean(sidebarOpen)}
            sx={iconButtonSx}
          >
            {sidebarOpen?(
              <KeyboardDoubleArrowLeftRoundedIcon sx={{fontSize:23}}/>
            ):(
              <KeyboardDoubleArrowRightRoundedIcon sx={{fontSize:23}}/>
            )}
          </IconButton>
        </Tooltip>

        <Button
          onClick={goToPanel}
          startIcon={<DashboardRoundedIcon sx={{fontSize:21}}/>}
          sx={{
            height:40,
            minWidth:{xs:40,sm:"auto"},
            px:{xs:0.8,sm:1.3},
            borderRadius:"10px",
            textTransform:"none",
            fontSize:13.5,
            fontWeight:600,
            color:textColor,
            boxShadow:"none",
            whiteSpace:"nowrap",
            "& .MuiButton-startIcon":{
              color:primaryColor,
              mr:{xs:0,sm:1},
              ml:0
            },
            "&:hover":{
              bgcolor:hoverColor,
              boxShadow:"none"
            }
          }}
        >
          <Box
            component="span"
            sx={{
              display:{xs:"none",sm:"inline"}
            }}
          >
            Panel de Control
          </Box>
        </Button>
      </Stack>

      <Stack
        direction="row"
        spacing={{xs:0.3,sm:0.7}}
        alignItems="center"
        sx={{
          minWidth:0,
          ml:"auto"
        }}
      >
        {!isMobile&&canViewEnterprise&&(
          <Tooltip title="Mi Compañía" arrow disableInteractive>
            <IconButton
              onClick={goToCompany}
              aria-label="Mi Compañía"
              sx={iconButtonSx}
            >
              <Box
                component="img"
                src={briefcaseIcon}
                alt=""
                sx={imageStyles}
              />
            </IconButton>
          </Tooltip>
        )}

        {!isMobile&&canViewVentas&&(
          <Tooltip title="Caja" arrow disableInteractive>
            <IconButton
              onClick={goToCaja}
              aria-label="Caja"
              sx={iconButtonSx}
            >
              <Box
                component="img"
                src={registerIcon}
                alt=""
                sx={imageStyles}
              />
            </IconButton>
          </Tooltip>
        )}

        {!isMobile&&(
          <Tooltip title={themeLabel} arrow disableInteractive>
            <IconButton
              onClick={handleToggleTheme}
              aria-label={themeLabel}
              sx={iconButtonSx}
            >
              {isDark?(
                <LightModeRoundedIcon sx={{fontSize:22}}/>
              ):(
                <DarkModeRoundedIcon sx={{fontSize:22}}/>
              )}
            </IconButton>
          </Tooltip>
        )}

        <Button
          onClick={event=>setAnchorEl(event.currentTarget)}
          aria-label="Abrir menú de usuario"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          sx={{
            textTransform:"none",
            borderRadius:"12px",
            px:{xs:0.5,sm:1},
            py:0.7,
            color:textColor,
            boxShadow:"none",
            minWidth:0,
            maxWidth:{xs:95,sm:340},
            "&:hover":{
              bgcolor:hoverColor,
              boxShadow:"none"
            }
          }}
        >
          <Stack
            direction="row"
            spacing={{xs:0.5,sm:1}}
            alignItems="center"
            sx={{minWidth:0}}
          >
            <Box
              sx={{
                position:"relative",
                flexShrink:0,
                width:34,
                height:34,
                overflow:"visible"
              }}
            >
              <Box
                component="img"
                src={userIcon}
                alt="Usuario"
                sx={{
                  width:34,
                  height:34,
                  objectFit:"contain",
                  display:"block",
                  filter:iconFilter
                }}
              />

              <Box
                sx={{
                  position:"absolute",
                  bottom:1,
                  right:1,
                  width:10,
                  height:10,
                  borderRadius:"50%",
                  bgcolor:dotColor,
                  border:`2px solid ${backgroundColor}`,
                  boxSizing:"border-box",
                  zIndex:2,
                  pointerEvents:"none"
                }}
              />
            </Box>

            {!isMobile&&(
              <Box
                sx={{
                  textAlign:"left",
                  maxWidth:{sm:190,md:250},
                  minWidth:0
                }}
              >
                <Typography
                  noWrap
                  sx={{
                    fontSize:13.5,
                    fontWeight:600,
                    color:textColor,
                    lineHeight:1.35
                  }}
                >
                  {user?.name||"Usuario"}
                </Typography>

                <Typography
                  noWrap
                  sx={{
                    fontSize:11.5,
                    fontWeight:400,
                    color:mutedColor,
                    lineHeight:1.35
                  }}
                >
                  {user?.email||"Sin correo"}
                </Typography>
              </Box>
            )}

            <KeyboardArrowDownRoundedIcon
              sx={{
                fontSize:19,
                color:mutedColor,
                flexShrink:0
              }}
            />
          </Stack>
        </Button>
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        disableScrollLock
        disableAutoFocusItem
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        keepMounted
        marginThreshold={8}
        anchorOrigin={{
          vertical:"bottom",
          horizontal:"right"
        }}
        transformOrigin={{
          vertical:"top",
          horizontal:"right"
        }}
        slotProps={{
          paper:{
            sx:{
              mt:0.8,
              width:230,
              maxWidth:"calc(100vw - 16px)",
              borderRadius:"12px",
              border:`1px solid ${borderColor}`,
              boxShadow:"0 8px 26px rgba(0,0,0,0.10)",
              bgcolor:backgroundColor,
              color:textColor,
              overflow:"hidden"
            }
          },
          list:{
            sx:{
              py:0.6,
              bgcolor:backgroundColor
            }
          }
        }}
      >
        {isMobile&&(
          <MenuItem
            onClick={handleToggleTheme}
            sx={menuItemSx}
          >
            {isDark?(
              <LightModeRoundedIcon sx={{fontSize:20,color:mutedColor}}/>
            ):(
              <DarkModeRoundedIcon sx={{fontSize:20,color:mutedColor}}/>
            )}
            {themeLabel}
          </MenuItem>
        )}

        {isMobile&&canViewVentas&&(
          <MenuItem
            onClick={goToCaja}
            sx={menuItemSx}
          >
            <Box
              component="img"
              src={registerIcon}
              alt=""
              sx={imageStyles}
            />
            Caja
          </MenuItem>
        )}

        {isMobile&&canViewEnterprise&&(
          <MenuItem
            onClick={goToCompany}
            sx={menuItemSx}
          >
            <Box
              component="img"
              src={briefcaseIcon}
              alt=""
              sx={imageStyles}
            />
            Mi Compañía
          </MenuItem>
        )}

        <MenuItem
          onClick={closeSession}
          sx={menuItemSx}
        >
          <LogoutRoundedIcon
            sx={{
              fontSize:20,
              color:mutedColor
            }}
          />
          Cerrar sesión
        </MenuItem>
      </Menu>
    </Box>
  );
}
